import { useEffect, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, SelectTrigger, Tooltip } from "@similarweb/ui-components";
import { Flex, Text, TruncatedText } from "@similarweb/ui-components/foundations";

import { useActivationSetFavicons } from "app/pages/activation-alerts/hooks/useActivationSetFavicons";

import { FilterOption } from "../FilterOption";
import { useMultiselectFilter } from "../hooks/useMultiselectFilter";
import type { CompetitorValueMap } from "../types";

import { CompetitorsFaviconIcon } from "./CompetitorsFaviconIcon";
import { SecondaryCompetitorsSection } from "./SecondaryCompetitorsSection";
import { useCompetitorsDisplayText } from "./useCompetitorsDisplayText";
import { useCompetitorsFilterStore } from "./useCompetitorsFilterStore";
import { useCompetitorsMap } from "./useCompetitorsMap";
import { useCompetitorsSearch } from "./useCompetitorsSearch";
import { useEffectiveSelectedCompetitors } from "./useEffectiveSelectedCompetitors";
import { useIsSecondaryDomainsAllowed } from "./useIsSecondaryDomainsAllowed";

export const CompetitorsFilter = () => {
    const { translate } = useTranslate();
    const { allCompetitorsValues, isLoading } = useCompetitorsMap();
    const setSelectedCompetitors = useCompetitorsFilterStore((s) => s.setSelectedCompetitors);
    const reset = useCompetitorsFilterStore((s) => s.reset);
    const isSecondaryAllowed = useIsSecondaryDomainsAllowed();
    const { selectedCompetitors } = useEffectiveSelectedCompetitors({ primaryCompetitorsOnly: !isSecondaryAllowed });

    const effectiveAllCompetitorsValues = useMemo<CompetitorValueMap>(() => {
        if (isSecondaryAllowed) {
            return allCompetitorsValues;
        }
        const map: CompetitorValueMap = new Map();
        allCompetitorsValues.forEach((value, key) => {
            if (value.kind !== "secondary") {
                map.set(key, value);
            }
        });
        return map;
    }, [allCompetitorsValues, isSecondaryAllowed]);

    const finalCompetitors = selectedCompetitors.size ? selectedCompetitors : effectiveAllCompetitorsValues;
    const isDefaultSelected =
        !selectedCompetitors.size || selectedCompetitors.size === effectiveAllCompetitorsValues.size;
    const isSingleSelected = selectedCompetitors.size === 1;

    const { isOpen, tempSelectedItems, onItemSelect, onAllItemsClick, onApply, onOpenChange } = useMultiselectFilter({
        initialItems: allCompetitorsValues,
        allItems: allCompetitorsValues,
        onApplyChanges: setSelectedCompetitors,
        appliedItems: finalCompetitors,
    });

    const { searchValue, setSearchValue, primaryOptions, secondaryOptions } =
        useCompetitorsSearch(allCompetitorsValues);
    const { displayText, tooltipContent, showTooltip } = useCompetitorsDisplayText({
        selectedCompetitors: finalCompetitors,
        isDefaultSelected,
    });

    const { favicons } = useActivationSetFavicons();

    const singleSelectedCompetitor = isSingleSelected ? Array.from(selectedCompetitors.values())[0] : null;

    useEffect(() => {
        return reset;
    }, [reset]);

    if (isLoading) {
        return null;
    }

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={onOpenChange}
        >
            <Combobox.Trigger>
                <SelectTrigger data-automation="competitors-filter-control">
                    <Tooltip
                        content={tooltipContent}
                        enabled={showTooltip}
                    >
                        <Flex
                            gap="8px"
                            padding="0 12px 0 16px"
                            alignItems="center"
                        >
                            <CompetitorsFaviconIcon
                                favicon={singleSelectedCompetitor ? favicons?.[singleSelectedCompetitor.id] : null}
                                domain={singleSelectedCompetitor?.label ?? undefined}
                            />
                            <TruncatedText
                                fontVariant="listItem1"
                                width="120px"
                                textAlign="start"
                            >
                                {displayText}
                            </TruncatedText>
                        </Flex>
                    </Tooltip>
                </SelectTrigger>
            </Combobox.Trigger>

            <Combobox.SearchInput
                value={searchValue}
                onChange={setSearchValue}
                placeholder={translate("activation.alerts.competitors.dropdown.search.placeholder")}
            />

            <Combobox.Content>
                <Combobox.Section key="competitors">
                    {!searchValue ? (
                        <Combobox.Option
                            onClick={onAllItemsClick}
                            textValue="all"
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                                width="100%"
                            >
                                <Text fontVariant="listItem1">
                                    {translate("activation.alerts.competitors.dropdown.all.competitors-with-count", {
                                        count: allCompetitorsValues.size,
                                    })}
                                </Text>
                                <Combobox.OptionCheckbox
                                    isChecked={tempSelectedItems.size === allCompetitorsValues.size}
                                    halfSelected={tempSelectedItems.size > 0}
                                />
                            </Flex>
                        </Combobox.Option>
                    ) : null}
                    {primaryOptions.map((item) => (
                        <FilterOption
                            key={item.id}
                            item={item}
                            onClick={() => onItemSelect(item)}
                            favicon={favicons?.[item.id]}
                        >
                            <Combobox.OptionCheckbox isChecked={tempSelectedItems.has(item.id)} />
                        </FilterOption>
                    ))}
                </Combobox.Section>
                <SecondaryCompetitorsSection
                    options={secondaryOptions}
                    tempSelectedItems={tempSelectedItems}
                    favicons={favicons}
                    onItemSelect={onItemSelect}
                />
            </Combobox.Content>
            <Combobox.ApplyButton onClick={onApply}>{translate("common.apply")}</Combobox.ApplyButton>
        </Combobox>
    );
};
